import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Store, Handshake, Globe, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal, ImageReveal } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";

const PERKS = [
  { icon: Store, title: "Stock the Range", body: "Full access to the GAVRA hardware catalogue with dealer pricing and priority restocking." },
  { icon: Handshake, title: "Marketing Support", body: "Point-of-sale displays, product imagery and co-branded campaign assets to help you sell." },
  { icon: Globe, title: "Listed Online", body: "Get featured on our dealer locator so drummers in your region can find you first." },
];

export default function Dealers() {
  const [dealers, setDealers] = useState([]);

  useEffect(() => {
    api.get("/dealers").then((r) => setDealers(r.data)).catch(() => setDealers([]));
  }, []);

  return (
    <div data-testid="dealers-page" className="min-h-screen bg-white pt-20">
      {/* hero */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 pt-16 md:pt-24">
        <Reveal>
          <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">Where To Buy</p>
          <h1 className="mt-4 max-w-4xl font-heading text-5xl font-black uppercase leading-[0.85] tracking-tighter md:text-8xl">
            Find A<br /><span className="text-gavra">Dealer</span>
          </h1>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-black/60 md:text-lg">
            GAVRA hardware is sold through a network of trusted drum specialists. Find your nearest
            authorized dealer, or apply to carry the range in your store.
          </p>
        </Reveal>
      </section>

      {/* dealer grid */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 py-16 md:py-24">
        {dealers.length === 0 ? (
          <div data-testid="dealers-empty" className="border border-black/10 p-16 text-center">
            <p className="font-heading text-2xl font-bold uppercase">No dealers listed yet</p>
            <p className="mt-2 text-sm text-black/50">Check back soon or contact us for stockists.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {dealers.map((d, i) => (
              <ImageReveal key={d.id}>
                <div data-testid={`dealer-card-${i}`} className="group flex h-full flex-col border border-black/10 p-6 transition-colors hover:border-gavra">
                  {d.image_url && (
                    <div className="mb-4 aspect-[16/9] overflow-hidden border border-black/10 bg-secondary">
                      <img src={d.image_url} alt={d.name} className="h-full w-full object-cover" />
                    </div>
                  )}
                  <span className="font-mono-hw text-[10px] uppercase tracking-widest text-gavra">{d.dealer_type}</span>
                  <h3 className="mt-3 font-heading text-2xl font-bold uppercase tracking-tight">{d.name}</h3>
                  {d.city && (
                    <p className="mt-4 flex items-center gap-2 text-sm text-black/60">
                      <MapPin className="h-4 w-4 text-gavra" /> {d.city}
                    </p>
                  )}
                  {d.address && <p className="mt-2 text-sm text-black/50">{d.address}</p>}
                  {d.phone && (
                    <p className="mt-2 flex items-center gap-2 font-mono-hw text-sm text-black/50">
                      <Phone className="h-3.5 w-3.5" /> {d.phone}
                    </p>
                  )}
                  {d.website && (
                    <a href={d.website} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-gavra hover:underline">
                      Visit website <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              </ImageReveal>
            ))}
          </div>
        )}
      </section>

      <EditorialMarquee text={["Become A Dealer", "Stock The Range", "Sell GAVRA"]} speed={45} />

      {/* become a dealer */}
      <section className="bg-black text-white grain">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-24 md:py-32">
          <Reveal>
            <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">Partner With Us</p>
            <h2 className="mt-4 font-heading text-4xl font-black uppercase leading-none tracking-tighter md:text-7xl">
              Become a<br />GAVRA dealer
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="border-t-2 border-gavra pt-6">
                  <p.icon className="h-8 w-8 text-gavra" strokeWidth={2} />
                  <h3 className="mt-5 font-heading text-2xl font-bold uppercase tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-white/60">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <Link
              to="/contact"
              data-testid="dealer-apply-btn"
              className="mt-14 inline-flex items-center gap-3 bg-gavra px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white hover:text-black"
            >
              Apply to become a dealer <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
